import React, { useMemo, useState } from 'react';
import { Check, ChevronLeft } from 'lucide-react';
import { t } from '../i18n';
import Modal from './ui/Modal';
import {
  FEATURED_CURRENCIES,
  currencyName,
  currencySymbol,
  listAllCurrencyCodes,
  resolveCurrency,
  viewerLocale,
} from '../utils/currency';

const ROW =
  'w-full min-h-12 px-3.5 rounded-[12px] border flex items-center gap-3 text-left transition-colors';

export default function CurrencyModal({ open, selected, onSelect, onClose }) {
  const [more, setMore] = useState(false);
  const current = resolveCurrency(selected);
  const locale = viewerLocale();

  const rest = useMemo(() => {
    const featured = new Set(FEATURED_CURRENCIES.map((c) => c.code));
    return listAllCurrencyCodes()
      .filter((code) => !featured.has(code))
      .map((code) => ({
        code,
        symbol: currencySymbol(code, locale),
        name: currencyName(code, locale),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, locale));
  }, [locale]);

  const pick = (code) => {
    onSelect?.(resolveCurrency(code));
    setMore(false);
    onClose?.();
  };

  const handleClose = () => {
    setMore(false);
    onClose?.();
  };

  const renderRow = (item, { selected: isOn } = {}) => (
    <button
      key={item.code}
      type="button"
      onClick={() => pick(item.code)}
      data-testid={`currency-option-${item.code}`}
      className={`${ROW} ${
        isOn ? 'bg-ink text-cream border-ink' : 'bg-transparent border-line text-ink hover:border-ink'
      }`}
    >
      <span className="w-8 shrink-0 text-[15px] font-medium tnum">{item.symbol}</span>
      <span className="flex-1 min-w-0 text-[14.5px] truncate">{item.name}</span>
      {isOn && <Check className="w-4 h-4 shrink-0" aria-hidden />}
    </button>
  );

  return (
    <Modal
      open={!!open}
      onClose={handleClose}
      title={t('currency.title')}
      compact
      zClass="z-[90]"
      testId="currency-modal"
    >
      <p className="text-[12.5px] text-mute leading-snug m-0 mb-3">
        {t('currency.hint')}
      </p>
      {more ? (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setMore(false)}
            data-testid="currency-more-back"
            className={`${ROW} bg-transparent border-line text-ink`}
          >
            <ChevronLeft className="w-4 h-4 shrink-0" />
            <span className="text-[14.5px]">{t('common.back')}</span>
          </button>
          {rest.map((item) => renderRow(item, { selected: item.code === current }))}
        </div>
      ) : (
        <div className="space-y-2">
          {FEATURED_CURRENCIES.map((item) => renderRow(item, { selected: item.code === current }))}
          <button
            type="button"
            onClick={() => setMore(true)}
            data-testid="currency-more"
            className={`${ROW} bg-transparent border-line text-ink hover:border-ink`}
          >
            <span className="w-8 shrink-0 text-[15px] text-mute">…</span>
            <span className="flex-1 text-[14.5px]">{t('currency.more')}</span>
          </button>
        </div>
      )}
    </Modal>
  );
}
